import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MessageSquare, ChevronLeft, Home } from "lucide-react";
import { HouseChatPanel } from "../app-property-detail/HouseChatPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatRelativeSync } from "@/lib/formatRelativeSync";
import { useHouseChats, HOUSE_CHATS_KEY } from "./useHouseChats";
import type { HouseChatSummary } from "./useHouseChats";
import { HouseChatArchive } from "./HouseChatArchive";

const itemKey = (item: HouseChatSummary) => item.propertyId == null
  ? `archive:${item.houseConversationId}` : `property:${item.propertyId}`;

// "House Chats" tab on the host Messages page: a master list of every house
// chat across the host's direct (self_managed) properties, with the selected
// property's chat + parking shown via the shared HouseChatPanel. Mobile shows
// the list, then drills into the detail (back arrow); md+ shows both panes and
// auto-selects the first house.
export function HouseChatsTab() {
  const [selected, setSelected] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const { data, isLoading } = useHouseChats();
  const list = data ?? [];
  const selectedItem = list.find((i) => itemKey(i) === selected) ?? null;

  // Desktop (md+) shows both panes, so land the host straight in the first
  // house instead of an empty "select a house" pane. Mobile keeps the list.
  useEffect(() => {
    if (!isMobile && selected == null && list.length > 0) {
      setSelected(itemKey(list[0]));
    }
  }, [isMobile, selected, list]);

  return (
    <div className="flex h-full min-h-0">
      {/* Master list */}
      <div
        className={`w-full flex-col overflow-y-auto border-r border-gray-200 md:flex md:w-80 ${
          selected != null ? "hidden md:flex" : "flex"
        }`}
      >
        {isLoading ? (
          <p className="p-4 text-sm text-gray-400">Loading…</p>
        ) : list.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">
            <Home className="mx-auto mb-2 h-6 w-6 text-gray-300" />
            No direct (self-managed) properties yet. House chats appear here once you have one.
          </div>
        ) : (
          <ul>
            {list.map((i) => {
              const unread = i.unreadCount > 0;
              return (
                <li key={itemKey(i)}>
                  <button
                    onClick={() => setSelected(itemKey(i))}
                    className={`flex w-full items-start gap-2 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 ${
                      selected === itemKey(i) ? "bg-blue-50" : ""
                    }`}
                    data-testid={`house-chat-row-${i.propertyId ?? `archive-${i.houseConversationId}`}`}
                  >
                    {/* Unread dot */}
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${unread ? "bg-blue-600" : "bg-transparent"}`}
                      aria-hidden="true"
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${unread ? "font-semibold text-gray-900" : "font-medium text-gray-900"}`}>
                          {i.propertyName || i.address || `Property ${i.propertyId}`}
                          {i.propertyId == null && <span className="ml-2 text-xs font-normal text-muted-foreground">Archived</span>}
                        </span>
                        {i.lastMessageAt && (
                          <span className="shrink-0 text-[11px] text-gray-400">
                            {formatRelativeSync(i.lastMessageAt)}
                          </span>
                        )}
                      </span>
                      <span className={`line-clamp-1 w-full text-xs ${unread ? "font-medium text-gray-700" : "text-gray-500"}`}>
                        {i.lastMessagePreview
                          ? `${i.lastSenderName ? `${i.lastSenderName}: ` : ""}${i.lastMessagePreview}`
                          : "No messages yet"}
                      </span>
                    </span>
                    {unread && (
                      <span
                        className="mt-0.5 shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                        data-testid={`house-chat-unread-${i.propertyId}`}
                      >
                        {i.unreadCount}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Detail — full-height flex column so the chat pane fills the space. */}
      <div
        className={`min-h-0 flex-1 flex-col ${
          selected != null ? "flex" : "hidden md:flex"
        }`}
      >
        {selectedItem ? (
          <>
            <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 p-4">
              <button
                onClick={() => setSelected(null)}
                className="md:hidden"
                aria-label="Back to house list"
                data-testid="button-house-chat-back"
              >
                <ChevronLeft className="h-5 w-5 text-gray-500" />
              </button>
              <h3 className="truncate text-base font-semibold text-gray-900">
                {selectedItem.propertyName || selectedItem.address || `Property ${selectedItem.propertyId}`}
              </h3>
            </div>
            <div className="min-h-0 flex-1 p-4">
              {selectedItem.propertyId == null && selectedItem.houseConversationId != null ? (
                <HouseChatArchive key={selectedItem.houseConversationId} conversationId={selectedItem.houseConversationId} />
              ) : selectedItem.propertyId != null ? <HouseChatPanel
                propertyId={selectedItem.propertyId}
                onRead={() => queryClient.invalidateQueries({ queryKey: HOUSE_CHATS_KEY })}
              /> : null}
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            <div className="text-center">
              <MessageSquare className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              Select a house to view its chat.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
